import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class PermissionService {
  private permissions: string[] = [];
  private loaded: boolean = false;
  private listeners: Array<() => void> = [];

  async loadPermissions(): Promise<string[]> {
    try {
      const response = await axios.get<string[]>(`${API_BASE_URL}/auth/me/permissions`, {
        withCredentials: true,
      });
      this.permissions = response.data;
      this.loaded = true;
      // Notify all listeners that permissions have been loaded
      this.notifyListeners();
      return this.permissions;
    } catch (error) {
      console.error('Hiba a jogosultságok betöltése során:', error);
      this.permissions = [];
      this.loaded = false;
      return [];
    }
  }

  onPermissionsChanged(callback: () => void): () => void {
    this.listeners.push(callback);
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  getPermissions(): string[] {
    return this.permissions;
  }

  hasPermission(permission: string): boolean {
    return this.permissions.includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(p => this.permissions.includes(p));
  }

  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(p => this.permissions.includes(p));
  }

  clearPermissions(): void {
    this.permissions = [];
    this.loaded = false;
    // Notify listeners that permissions have been cleared
    this.notifyListeners();
  }

  isLoaded(): boolean {
    return this.loaded;
  }
}

export const permissionService = new PermissionService();

// Permission constants matching backend
export const Permissions = {
  // Általános
  ViewDashboard: 'Permissions.ViewDashboard',
  ViewReports: 'Permissions.ViewReports',
  
  // Időráfordítások
  ViewTimeTracking: 'Permissions.ViewTimeTracking',
  ViewAllTimeTracking: 'Permissions.ViewAllTimeTracking',
  CreateTimeTracking: 'Permissions.CreateTimeTracking',
  EditTimeTracking: 'Permissions.EditTimeTracking',
  DeleteTimeTracking: 'Permissions.DeleteTimeTracking',

  // Szabadságok
  ViewLeaves: 'Permissions.ViewLeaves',
  CreateLeaves: 'Permissions.CreateLeaves',
  EditLeaves: 'Permissions.EditLeaves',
  DeleteLeaves: 'Permissions.DeleteLeaves',

  // Céges autó
  ViewCompanyCar: 'Permissions.ViewCompanyCar',
  CreateCompanyCar: 'Permissions.CreateCompanyCar',
  EditCompanyCar: 'Permissions.EditCompanyCar',
  DeleteCompanyCar: 'Permissions.DeleteCompanyCar',
  
  // Ajánlatok
  ViewOffers: 'Permissions.ViewOffers',
  CreateOffers: 'Permissions.CreateOffers',
  EditOffers: 'Permissions.EditOffers',
  DeleteOffers: 'Permissions.DeleteOffers',
  
  // Iktatások
  ViewRegistrations: 'Permissions.ViewRegistrations',
  CreateRegistrations: 'Permissions.CreateRegistrations',
  EditRegistrations: 'Permissions.EditRegistrations',
  DeleteRegistrations: 'Permissions.DeleteRegistrations',
  
  // Megrendelők
  ViewClients: 'Permissions.ViewClients',
  CreateClients: 'Permissions.CreateClients',
  EditClients: 'Permissions.EditClients',
  DeleteClients: 'Permissions.DeleteClients',
  
  // Munkaszámok
  ViewWorkNumbers: 'Permissions.ViewWorkNumbers',
  CreateWorkNumbers: 'Permissions.CreateWorkNumbers',
  EditWorkNumbers: 'Permissions.EditWorkNumbers',
  DeleteWorkNumbers: 'Permissions.DeleteWorkNumbers',
  ViewWorkNumberParticipants: 'Permissions.ViewWorkNumberParticipants',
  ViewAllTaskManagerTasks: 'Permissions.ViewAllTaskManagerTasks',
  ExportFolyamatlap: 'Permissions.ExportFolyamatlap',

  // Projekttervezés
  ViewProjectPlanning: 'Permissions.ViewProjectPlanning',
  CreateProjectPlanning: 'Permissions.CreateProjectPlanning',
  EditProjectPlanning: 'Permissions.EditProjectPlanning',
  DeleteProjectPlanning: 'Permissions.DeleteProjectPlanning',
  
  // Felhasználók és szerepek
  ViewUsers: 'Permissions.ViewUsers',
  CreateUsers: 'Permissions.CreateUsers',
  EditUsers: 'Permissions.EditUsers',
  DeleteUsers: 'Permissions.DeleteUsers',
  ViewRoles: 'Permissions.ViewRoles',
  CreateRoles: 'Permissions.CreateRoles',
  EditRoles: 'Permissions.EditRoles',
  DeleteRoles: 'Permissions.DeleteRoles',
  ManageUserRoles: 'Permissions.ManageUserRoles',

  // Előfizetések és költségek
  ViewBilling: 'Permissions.ViewBilling',
  ManageBilling: 'Permissions.ManageBilling',
};
